import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle2, UserX, XCircle, Loader2, Clock } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Weekly calendar view for /admin/foglalasok.
 * Rows = time slots (env SLOT_TIMES default 08:00–19:00). Columns = 7 days.
 * Cells show booking status + client name; pending cells expose the same
 * 3 action buttons (Megjelent / Nem jelent meg / Lemondás).
 */

const DEFAULT_TIMES = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00'];
const DAY_NAMES = ['Vasárnap','Hétfő','Kedd','Szerda','Csütörtök','Péntek','Szombat'];
const SHORT = ['V','H','K','Sze','Cs','P','Szo'];

const toIsoDate = (d) => d.toISOString().slice(0, 10);
const startOfWeek = (d) => {
  const nd = new Date(d);
  nd.setHours(0, 0, 0, 0);
  const day = nd.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day; // Monday-start
  nd.setDate(nd.getDate() + diff);
  return nd;
};
const addDays = (d, n) => { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; };

const statusMeta = {
  pending:   { color: 'bg-amber-100 border-amber-300 text-amber-900',    label: 'függő'      },
  attended:  { color: 'bg-emerald-100 border-emerald-300 text-emerald-900', label: 'megjelent' },
  no_show:   { color: 'bg-red-100 border-red-300 text-red-900',           label: 'nem jött'  },
  cancelled: { color: 'bg-gray-100 border-gray-200 text-gray-500 line-through', label: 'lemondva' },
};

const BookingsCalendarView = ({ bookings = [], busy, onAction }) => {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));

  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = addDays(weekStart, i);
      out.push({ date: toIsoDate(d), obj: d });
    }
    return out;
  }, [weekStart]);

  const weekRange = `${days[0].obj.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })} – ${days[6].obj.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}`;

  // Group bookings by "date|time" for O(1) cell lookup.
  const grid = useMemo(() => {
    const m = {};
    for (const b of bookings) {
      m[`${b.slot_date}|${b.slot_time}`] = b;
    }
    return m;
  }, [bookings]);

  const today = toIsoDate(new Date());

  const renderCell = (dateIso, time) => {
    const b = grid[`${dateIso}|${time}`];
    if (!b) {
      return <div className="h-16 rounded border border-dashed border-gray-800 bg-gray-950/40" />;
    }
    const meta = statusMeta[b.status] || statusMeta.pending;
    const busyThis = busy === b.booking_id;
    return (
      <div
        data-testid={`admin-cal-cell-${b.booking_id}`}
        className={`h-16 rounded border p-1.5 text-[11px] flex flex-col justify-between ${meta.color} overflow-hidden`}
      >
        <div className="flex items-center justify-between gap-1 min-w-0">
          <span className="font-bold truncate">
            {b.user?.name || b.user?.email?.split('@')[0] || '—'}
          </span>
          <span className="text-[9px] font-bold uppercase tracking-widest shrink-0 opacity-70">
            {meta.label}
          </span>
        </div>
        {b.status === 'pending' && (
          <div className="flex items-center gap-1 justify-end">
            <button
              data-testid={`admin-cal-cancel-${b.booking_id}`}
              disabled={busyThis}
              onClick={() => onAction(b.booking_id, 'cancel')}
              title="Lemondás"
              className="w-5 h-5 rounded bg-white/60 hover:bg-white text-gray-600 flex items-center justify-center"
            >
              <XCircle size={12} />
            </button>
            <button
              data-testid={`admin-cal-noshow-${b.booking_id}`}
              disabled={busyThis}
              onClick={() => onAction(b.booking_id, 'no_show')}
              title="Nem jelent meg"
              className="w-5 h-5 rounded bg-white/60 hover:bg-red-500 hover:text-white text-red-700 flex items-center justify-center"
            >
              <UserX size={12} />
            </button>
            <button
              data-testid={`admin-cal-attended-${b.booking_id}`}
              disabled={busyThis}
              onClick={() => onAction(b.booking_id, 'attended')}
              title="Megjelent"
              className="w-5 h-5 rounded bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center"
            >
              {busyThis ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={12} />}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="admin-bookings-calendar">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button
            data-testid="admin-cal-prev-week"
            variant="ghost"
            size="sm"
            onClick={() => setWeekStart((d) => addDays(d, -7))}
            className="text-gray-300 hover:text-white"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            data-testid="admin-cal-this-week"
            variant="ghost"
            size="sm"
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            className="text-gray-300 hover:text-white text-xs"
          >
            Ez a hét
          </Button>
          <Button
            data-testid="admin-cal-next-week"
            variant="ghost"
            size="sm"
            onClick={() => setWeekStart((d) => addDays(d, 7))}
            className="text-gray-300 hover:text-white"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
        <div className="text-sm text-gray-300 font-semibold">{weekRange}</div>
      </div>

      <div className="overflow-x-auto -mx-3 md:mx-0">
        <div className="min-w-[900px] px-3 md:px-0">
          {/* Header row */}
          <div className="grid grid-cols-[70px_repeat(7,minmax(0,1fr))] gap-1 mb-1">
            <div />
            {days.map((d, i) => (
              <div
                key={d.date}
                className={`text-center py-1.5 rounded ${d.date === today ? 'bg-yellow-600/25 text-yellow-100' : 'bg-gray-900 text-gray-400'}`}
              >
                <div className="text-[10px] font-bold uppercase tracking-widest">
                  {SHORT[(i + 1) % 7]}
                </div>
                <div className="text-xs font-semibold">
                  {d.obj.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}
                </div>
              </div>
            ))}
          </div>

          {/* Time rows */}
          {DEFAULT_TIMES.map((time) => (
            <div key={time} className="grid grid-cols-[70px_repeat(7,minmax(0,1fr))] gap-1 mb-1">
              <div className="flex items-center justify-end pr-2 text-xs text-gray-500 font-mono">
                <Clock size={10} className="mr-1" />{time}
              </div>
              {days.map((d) => (
                <div key={`${d.date}-${time}`}>
                  {renderCell(d.date, time)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BookingsCalendarView;
